import {Grid, Typography, Stack, IconButton, Box, Skeleton} from "@mui/material"
import {FC, useEffect, useState} from "react"
import CarouselItem from "../../molecules/CarouselItem/CarouselItem"
import {CarouselProps} from "./types"
import {CarouselItemProps} from "../../molecules/CarouselItem/types"
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded"
import {ArrowNext, ArrowPrev} from "../../../assets"
import {useClientSessionStore} from "../../../stores/clientSessionStore"
import ErrorMessage from "../../molecules/ErrorMessage/ErrorMessage"
import TextSeparator from "../../atoms/separator/TextSeparator"

const Carousel: FC<CarouselProps> = (props) => {
  const {
    dataSource,
    title,
    style,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
    refetch,
  } = props

  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const dotStyle = {
    activeDot: {fontSize: 10, color: "#1E4B59"},
    deactiveDot: {fontSize: 8, color: "#999999", opacity: 0.5},
  }
  const [hoverLeft, setHoverLeft] = useState(false)
  const [hoverRight, setHoverRight] = useState(false)

  const handleMouseEnter = (dir: string) =>
    dir == "left" ? setHoverLeft(true) : setHoverRight(true)
  const handleMouseLeave = (dir: string) =>
    dir == "left" ? setHoverLeft(false) : setHoverRight(false)

  const [currentIndex, setCurrentIndex] = useState(0)

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 4) % dataSource.length)
  }

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 4 + dataSource.length) % dataSource.length,
    )
  }

  const handleDotClick = (index: number) => {
    setCurrentIndex((index * 4) % dataSource.length)
  }

  const [currentVisibleItems, setCurrentVisibleItems] = useState<
    CarouselItemProps[]
  >([])

  useEffect(() => {
    const visibleItems = dataSource
      .slice(currentIndex, currentIndex + 4)
      .concat(
        dataSource.slice(
          0,
          Math.max(0, 4 - (dataSource.length - currentIndex)),
        ),
      )

    setCurrentVisibleItems([...visibleItems])
  }, [currentIndex])

  return (
    <>
      {!sessionClient ||
      isLoading ||
      isFetching ||
      isRefetching ||
      (!dataSource && !isError && !isLoadingError && !isRefetchError) ? (
        <Grid
          container
          sx={{
            pl: 2,
          }}
        >
          <Grid size={12}>
            <Typography align="left" style={style?.titleStyle}>
              {title}
            </Typography>
          </Grid>
          <Grid size={12} sx={{px: 1, mt: 1}}>
            <Stack direction="row" spacing={4}>
              <Grid
                container
                width="100%"
                sx={{columnGap: style?.mainStyle?.columnGap || 1}}
              >
                {[1, 2, 3, 4]?.map((item, index) => (
                  <Grid key={index} size={2.8}>
                    <Stack
                      id={"carousel-item-" + index}
                      spacing={1}
                      direction="column"
                    >
                      <Box
                        sx={{
                          textDecoration: "none",
                        }}
                      >
                        <Box
                          width="100%"
                          justifyContent="left"
                          marginBottom="12px"
                        >
                          <Stack direction="row" width="100%" color="#242D35">
                            <Box width="100%">
                              <Skeleton
                                animation="wave"
                                variant="text"
                                height={26}
                              />
                            </Box>
                            <TextSeparator
                              separator="|"
                              distanceX="7px"
                              fontSize="12px"
                              paddingTop="2.5px"
                            />
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                              }}
                            >
                              <Skeleton
                                animation="wave"
                                variant="text"
                                height={26}
                                width="100%"
                              />
                            </Box>
                          </Stack>
                        </Box>
                        <Box>
                          <Skeleton
                            animation="wave"
                            variant="rectangular"
                            height={108}
                          />
                        </Box>
                        <Box marginTop="7px">
                          <Skeleton
                            animation="wave"
                            variant="text"
                            height={26}
                            style={{
                              marginBottom: "-4px",
                            }}
                          />
                          <Skeleton
                            animation="wave"
                            variant="text"
                            height={26}
                            style={{
                              marginBottom: "-4px",
                            }}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>
          <Grid size={12} alignContent="center" marginTop="10px">
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <IconButton
                onClick={handlePrev}
                onMouseOver={() => handleMouseEnter("left")}
                onMouseLeave={() => handleMouseLeave("left")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowPrev
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverLeft ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(3)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 12
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(4)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 16
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(0)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 0
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(1)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 4
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(2)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 8
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={handleNext}
                onMouseOver={() => handleMouseEnter("right")}
                onMouseLeave={() => handleMouseLeave("right")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowNext
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverRight ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      ) : !dataSource ||
        isError ||
        isLoadingError ||
        isRefetchError ||
        !Array.isArray(dataSource) ||
        dataSource.length === 0 ? (
        <Grid
          container
          sx={{
            pl: 2,
            filter: "blur(0px)",
          }}
        >
          <Grid size={12}>
            <Typography align="left" style={style?.titleStyle}>
              {title}
            </Typography>
          </Grid>
          <Grid size={12} sx={{px: 1, mt: 1}}>
            <Stack direction="row" spacing={4}>
              <Grid
                container
                width="100%"
                sx={{
                  columnGap: style?.mainStyle?.columnGap || 1,
                  filter: "blur(5px)",
                }}
              >
                {[1, 2, 3, 4]?.map((item, index) => (
                  <Grid size={2.8} key={index}>
                    <Stack
                      id={"carousel-item-" + index}
                      spacing={1}
                      direction="column"
                    >
                      <Box
                        sx={{
                          textDecoration: "none",
                        }}
                      >
                        <Box
                          width="100%"
                          justifyContent="left"
                          marginBottom="12px"
                        >
                          <Stack direction="row" width="100%" color="#242D35">
                            <Box width="100%">
                              <Skeleton
                                animation="wave"
                                variant="text"
                                height={26}
                              />
                            </Box>
                            <TextSeparator
                              separator="|"
                              distanceX="7px"
                              fontSize="12px"
                              paddingTop="2.5px"
                            />
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                              }}
                            >
                              <Skeleton
                                animation="wave"
                                variant="text"
                                height={26}
                                width="100%"
                              />
                            </Box>
                          </Stack>
                        </Box>
                        <Box>
                          <Skeleton
                            animation="wave"
                            variant="rectangular"
                            height={108}
                          />
                        </Box>
                        <Box marginTop="7px">
                          <Skeleton
                            animation="wave"
                            variant="text"
                            height={26}
                            style={{
                              marginBottom: "-4px",
                            }}
                          />
                          <Skeleton
                            animation="wave"
                            variant="text"
                            height={26}
                            style={{
                              marginBottom: "-4px",
                            }}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>
          <Grid size={12} alignContent="center" marginTop="10px">
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <IconButton
                onClick={handlePrev}
                onMouseOver={() => handleMouseEnter("left")}
                onMouseLeave={() => handleMouseLeave("left")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowPrev
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverLeft ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(3)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 12
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(4)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 16
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(0)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 0
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(1)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 4
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(2)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 8
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={handleNext}
                onMouseOver={() => handleMouseEnter("right")}
                onMouseLeave={() => handleMouseLeave("right")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowNext
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverRight ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
            </Stack>
          </Grid>
          <ErrorMessage
            message={"This content is not available right now"}
            messageStyle={{fontSize: "14px", color: "#595958"}}
            refetch={refetch!}
          ></ErrorMessage>
        </Grid>
      ) : (
        <Grid
          container
          sx={{
            pl: 2,
          }}
        >
          <Grid size={12}>
            <Typography align="left" style={style?.titleStyle}>
              {title}
            </Typography>
          </Grid>
          <Grid size={12} sx={{px: 1, mt: 1}}>
            <Stack direction="row" spacing={4}>
              <Grid
                container
                width="100%"
                sx={{columnGap: style?.mainStyle?.columnGap || 1}}
              >
                {currentVisibleItems.length > 0 &&
                  currentVisibleItems.map((item, index) => (
                    <Grid
                      key={index}
                      size={2.8}
                      sx={{
                        "&:hover": {background: "#EEEEEE", borderRadius: 1},
                        '&:hover [data-text-hover="true"] p': {
                          textDecoration: "underline",
                          color: "#242D35",
                        },
                        paddingTop: 1,
                        paddingBottom: "21.3px",
                        px: 1,
                      }}
                    >
                      <CarouselItem
                        styles={style?.itemStyle}
                        articleId={item.articleId}
                        index={index}
                        title={item.title}
                        imageLargePath={item.imageLargePath}
                        imageMediumPath={item.imageMediumPath}
                        imageSmallPath={item.imageSmallPath}
                        author={item.author}
                        date={item.date}
                        itemUrl={item.itemUrl}
                        key={index}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Stack>
          </Grid>
          <Grid size={12} alignContent="center" marginTop="10px">
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <IconButton
                onClick={handlePrev}
                onMouseOver={() => handleMouseEnter("left")}
                onMouseLeave={() => handleMouseLeave("left")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowPrev
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverLeft ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(3)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 12
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(4)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 16
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(0)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 0
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(1)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 4
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={() => handleDotClick(2)}
                sx={{
                  "&:hover": {background: "#FFFFFF"},
                  "&&": {marginLeft: "5.8px"},
                  padding: 0,
                }}
              >
                <FiberManualRecordRoundedIcon
                  sx={
                    currentIndex == 8
                      ? dotStyle.activeDot
                      : dotStyle.deactiveDot
                  }
                />
              </IconButton>
              <IconButton
                onClick={handleNext}
                onMouseOver={() => handleMouseEnter("right")}
                onMouseLeave={() => handleMouseLeave("right")}
                sx={{"&:hover": {background: "#FFFFFF"}}}
              >
                <ArrowNext
                  style={{
                    fontSize: 16,
                  }}
                  color={hoverRight ? "#6A6A69" : "#CDCDCD"}
                />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default Carousel
